import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, Eye, RefreshCw, Calendar, TrendingUp, Globe } from 'lucide-react';
import api from '../services/api';
import OverviewStats from './analytics/OverviewStats';
import TrafficChart from './analytics/TrafficChart';
import GeographicChart from './analytics/GeographicChart';
import PageAnalytics from './analytics/PageAnalytics';
import VisitorDetails from './analytics/VisitorDetails';

function AnalyticsViewer() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Analytics data state
  const [visitorStats, setVisitorStats] = useState(null);
  const [geographicData, setGeographicData] = useState(null);
  const [trafficTimeline, setTrafficTimeline] = useState(null);
  const [popularPages, setPopularPages] = useState(null);
  const [userAgentStats, setUserAgentStats] = useState(null);

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: BarChart3,
      description: 'Key metrics and statistics',
    },
    {
      id: 'traffic',
      label: 'Traffic Timeline',
      icon: TrendingUp,
      description: 'Visitors and page views over time',
    },
    {
      id: 'geographic',
      label: 'Geographic',
      icon: Globe,
      description: 'Visitor locations and countries',
    },
    {
      id: 'pages',
      label: 'Popular Pages',
      icon: Eye,
      description: 'Most visited pages and time on page',
    },
    {
      id: 'visitors',
      label: 'Visitor Details',
      icon: Users,
      description: 'Browser, OS, and device breakdown',
    },
  ];

  const timeRangeOptions = [
    { value: 'hour', label: 'Last Hour' },
    { value: 'day', label: 'Last 24 Hours' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        timeRange: timeRange === 'custom' ? 'day' : timeRange,
        startDate: timeRange === 'custom' ? startDate : null,
        endDate: timeRange === 'custom' ? endDate : null,
      };

      // Load all analytics data in parallel
      const [
        visitorStatsResponse,
        geographicResponse,
        trafficResponse,
        pagesResponse,
        userAgentsResponse,
      ] = await Promise.all([
        api.getVisitorStats(params.timeRange, params.startDate, params.endDate),
        api.getGeographicData(params.timeRange, params.startDate, params.endDate),
        api.getTrafficTimeline(params.timeRange, params.startDate, params.endDate),
        api.getPopularPages(params.timeRange, params.startDate, params.endDate),
        api.getUserAgentStats(params.timeRange, params.startDate, params.endDate),
      ]);

      setVisitorStats(visitorStatsResponse.data);
      setGeographicData(geographicResponse.data);
      setTrafficTimeline(trafficResponse.data);
      setPopularPages(pagesResponse.data);
      setUserAgentStats(userAgentsResponse.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [timeRange, startDate, endDate]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Auto-refresh functionality
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadAnalyticsData, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loadAnalyticsData]);

  const handleTimeRangeChange = newTimeRange => {
    setTimeRange(newTimeRange);
    if (newTimeRange !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleRefresh = () => {
    loadAnalyticsData();
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
            <span className="text-gray-600">Loading analytics data...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return <OverviewStats data={visitorStats} />;
      case 'traffic':
        return <TrafficChart data={trafficTimeline} timeRange={timeRange} />;
      case 'geographic':
        return <GeographicChart data={geographicData} />;
      case 'pages':
        return <PageAnalytics data={popularPages} />;
      case 'visitors':
        return <VisitorDetails data={userAgentStats} />;
      default:
        return <OverviewStats data={visitorStats} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Track visitor behavior and site performance</p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Auto-refresh toggle */}
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-600">Auto-refresh</span>
          </label>

          {/* Manual refresh */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Time Range:</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {timeRangeOptions.map(option => (
              <button
                key={option.value}
                onClick={() => handleTimeRangeChange(option.value)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  timeRange === option.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {timeRange === 'custom' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              />
            </div>
          )}
        </div>

        {lastUpdated && (
          <div className="mt-2 text-sm text-gray-500">
            Last updated:{' '}
            {lastUpdated.toLocaleString('en-AU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
              timeZone: 'Australia/Melbourne',
            })}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">{renderTabContent()}</div>
      </div>
    </div>
  );
}

export default AnalyticsViewer;
